/**
 * Offline Action Queue
 *
 * Persists queued actions in IndexedDB. When the browser comes back online
 * each action is replayed in order. Failed items are kept for retry.
 */

const DB_NAME = "stdavid-offline-queue";
const STORE_NAME = "actions";
const DB_VERSION = 1;

export interface QueuedAction {
  id: string;
  type: "approval" | "upload" | "insert" | "update" | "rpc";
  /** Human-readable label shown in the UI */
  label: string;
  /** Serialisable payload that the executor receives */
  payload: Record<string, unknown>;
  createdAt: string;
  /** Number of failed sync attempts */
  retries: number;
  /** Last error message (if any) */
  lastError?: string;
  /** Earliest ISO timestamp the item is allowed to retry (exponential backoff) */
  nextRetryAt?: string;
  /** True when MAX_RETRIES is reached and the item is awaiting manual resolution */
  needsManualResolution?: boolean;
}

// ---------- IndexedDB helpers ----------

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueueAction(
  action: Omit<QueuedAction, "id" | "createdAt" | "retries">
): Promise<QueuedAction> {
  const db = await openDB();
  const item: QueuedAction = {
    ...action,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    retries: 0,
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(item);
    tx.oncomplete = () => {
      window.dispatchEvent(new CustomEvent("offline-queue-changed"));
      resolve(item);
    };
    tx.onerror = () => reject(tx.error);
  });
}

export async function getAllQueued(): Promise<QueuedAction[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result ?? []);
    req.onerror = () => reject(req.error);
  });
}

export async function removeAction(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => {
      window.dispatchEvent(new CustomEvent("offline-queue-changed"));
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

export async function updateAction(action: QueuedAction): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(action);
    tx.oncomplete = () => {
      window.dispatchEvent(new CustomEvent("offline-queue-changed"));
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

export async function clearQueue(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).clear();
    tx.oncomplete = () => {
      window.dispatchEvent(new CustomEvent("offline-queue-changed"));
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}
